interface BadgeProps {
  learnerName: string
  assessmentTitle: string
  dateTaken: string
  authenticationScore: number
  passed: boolean
}

export default function Badge({
  learnerName,
  assessmentTitle,
  dateTaken,
  authenticationScore,
  passed,
}: BadgeProps) {
  const accent = passed
    ? { bg: 'from-green-600 to-green-800', text: 'text-green-300', ring: 'ring-green-500' }
    : { bg: 'from-yellow-600 to-yellow-800', text: 'text-yellow-300', ring: 'ring-yellow-500' }

  return (
    <div
      id="badge-container"
      className={`badge-container rounded-2xl p-8 relative overflow-hidden ${accent.ring} ring-2`}
    >
      <div className="absolute inset-0 opacity-10">
        <svg className="w-full h-full" viewBox="0 0 400 400">
          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" />
          </pattern>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-gradient-radial ${accent.bg} opacity-20 blur-3xl`} />

      <div className="relative z-10">
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <svg className="w-6 h-6 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
            <span className="text-sm text-primary-300 font-medium tracking-wide">{assessmentTitle}</span>
          </div>
          <h2 className="text-lg text-gray-300">This certifies that</h2>
        </div>

        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-white mb-1">{learnerName}</h1>
          <div className="w-32 h-0.5 bg-gradient-to-r from-transparent via-primary-500 to-transparent mx-auto" />
        </div>

        <div className="text-center mb-6">
          <p className="text-gray-400 mb-3">has finished the ReallyAvatar Assessment</p>
          <div className={`inline-flex items-center justify-center bg-gradient-to-r ${accent.bg} px-8 py-3 rounded-xl`}>
            <span className="text-xl font-semibold text-white">Integrity Verified</span>
          </div>
        </div>

        <div className="text-center mb-6">
          <p className="text-gray-400 italic max-w-md mx-auto">
            Proctored assessment completed with verified identity and integrity monitoring.
          </p>
        </div>

        <div className="flex items-center justify-between pt-6 border-t border-gray-700">
          <div>
            <span className="text-xs text-gray-500 block">Date Issued</span>
            <span className="text-sm text-gray-300">{dateTaken}</span>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-2">
              {passed ? (
                <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              )}
              <span className={`text-sm font-medium ${passed ? 'text-green-400' : 'text-yellow-400'}`}>
                {passed ? 'Verified' : 'Unverified'}
              </span>
            </div>
            <span className="text-xs text-gray-500">Integrity Score: {authenticationScore}%</span>
          </div>
          <div className="text-right">
            <span className="text-xs text-gray-500 block">Powered by</span>
            <span className="text-sm text-primary-400 font-medium">ReallyAssess</span>
          </div>
        </div>
      </div>
    </div>
  )
}
